import ClarityLogo from "@/assets/clarity-logo-light.svg";
import DtoolsPdfPackageClosingPage from "@/components/sales/dtools/DtoolsPdfPackageClosingPage";
import DtoolsPdfPackageCoverPage from "@/components/sales/dtools/DtoolsPdfPackageCoverPage";
import { DtoolsPdfPackageProvider } from "@/components/sales/dtools/DtoolsPdfPackageContext";
import DtoolsPdfOptionQuotationSection from "@/components/sales/dtools/DtoolsPdfOptionQuotationSection";
import DtoolsPdfProposalSummarySection, {
  type DtoolsPdfSummarySystemRow,
} from "@/components/sales/dtools/DtoolsPdfProposalSummarySection";
import DtoolsPdfTermsSection from "@/components/sales/dtools/DtoolsPdfTermsSection";
import type { DtoolsPdfQuotationHeaderInfo } from "@/components/sales/dtools/DtoolsPdfQuotationPageHeader";
import { formatDtoolsMoney } from "@/lib/dtoolsJsonFormat";
import { PROPOSAL_COPY_FONT } from "@/lib/proposalTheme";
import { PROPOSAL_PDF_PACKAGE_SCREEN_HIDDEN_CLASS } from "@/lib/proposalPdfPrint";
import type { DtoolsDisplaySettings, DtoolsPortalPaymentTerm } from "@shared/dtoolsDisplaySettings";
import { DTOOLS_CUSTOMER_PREVIEW_VISIBILITY_AUDIENCE, filterVisibleDtoolsSystems } from "@shared/dtoolsOptionVisibility";
import {
  buildDtoolsPdfPackageCover,
  buildDtoolsPdfPackageOptions,
  formatDtoolsPdfPackageDate,
  resolveDtoolsPdfPackageRevisionNumber,
  type DtoolsPdfPackageMeta,
} from "@shared/dtoolsPdfPackage";
import type { DtoolsProposalPresentation } from "@shared/dtoolsProposalPresentation";
import { sortedProposalDisclaimers } from "@shared/dtoolsProposalPresentation";
import type { ProposalCustomerPricingDisplay } from "@shared/proposalCustomerPricing";
import { buildV2CustomerProposalView } from "@shared/dtoolsV2CustomerProposalView";
import { resolveDtoolsCustomerTabGate } from "@shared/dtoolsSystemClassification";
import {
  enrichDtoolsReviewBuildContext,
  buildDtoolsReviewProposalTotals,
  buildDtoolsReviewSystemView,
  type DtoolsReviewBuildContext,
} from "@shared/dtoolsReviewNavigation";
import type { DtoolsReviewFooter, DtoolsReviewSystem } from "@shared/dtoolsStagingTransform";
import { resolveProposalCustomerPricingDisplay } from "@shared/proposalCustomerPricing";
import type { DtoolsSystemClassificationsMap, DtoolsTabModelVersion } from "@shared/dtoolsSystemClassification";
import { useMemo } from "react";

type Props = {
  systems: DtoolsReviewSystem[];
  footer: DtoolsReviewFooter;
  reviewContext?: DtoolsReviewBuildContext;
  presentation?: DtoolsProposalPresentation;
  paymentTerms?: DtoolsPortalPaymentTerm[];
  displaySettings?: DtoolsDisplaySettings;
  tabModelVersion?: DtoolsTabModelVersion;
  systemClassifications?: DtoolsSystemClassificationsMap;
  parsedMetadataJson?: unknown;
  customerPricing?: ProposalCustomerPricingDisplay | null;
  retailPricingJson?: {
    listTotal?: number | null;
    originalListPrice?: number | null;
    agreedPriceSgd?: number | null;
    agreedPrice?: number | null;
  } | null;
  totalAmount?: number | string | null;
  cover: {
    customerName: string;
    proposalTitle: string;
    proposalRef: string;
  };
  pdfPackageMeta?: DtoolsPdfPackageMeta;
  createdAt?: string | null;
  linkBExpiresAt?: string | Date | null;
  approvedAt?: string | Date | null;
  status?: string | null;
  pipelineStage?: string | null;
};

export default function DtoolsProposalPdfPackage({
  systems,
  footer,
  reviewContext,
  presentation,
  paymentTerms = [],
  displaySettings = {},
  tabModelVersion,
  systemClassifications,
  parsedMetadataJson,
  customerPricing: customerPricingProp,
  retailPricingJson,
  totalAmount,
  cover,
  pdfPackageMeta,
  createdAt,
  linkBExpiresAt,
  approvedAt,
  status,
  pipelineStage,
}: Props) {
  const previewVisibilityAudience = DTOOLS_CUSTOMER_PREVIEW_VISIBILITY_AUDIENCE;
  const hideItemPricing = displaySettings.hideItemPricing === true;
  const alternativeOptions = displaySettings.alternativeOptions === true;

  const tabGate = useMemo(
    () =>
      resolveDtoolsCustomerTabGate({
        systems,
        tabModelVersion,
        parsedMetadataJson,
        systemClassifications,
      }),
    [systems, tabModelVersion, parsedMetadataJson, systemClassifications],
  );

  const enrichedContext = useMemo(
    () =>
      enrichDtoolsReviewBuildContext(systems, {
        ...reviewContext,
        presentation: reviewContext?.presentation ?? presentation,
        systemClassifications: tabGate.systemClassifications,
      }),
    [systems, reviewContext, presentation, tabGate.systemClassifications],
  );

  const isV2 = tabGate.useV2TabModel;

  const v2View = useMemo(
    () =>
      isV2
        ? buildV2CustomerProposalView({
            systems,
            classifications: tabGate.systemClassifications,
            displaySettings,
            reviewContext: enrichedContext,
            audience: previewVisibilityAudience,
          })
        : null,
    [isV2, systems, tabGate.systemClassifications, displaySettings, enrichedContext, previewVisibilityAudience],
  );

  const customerPricing = useMemo(() => {
    if (customerPricingProp) return customerPricingProp;
    const overrideTotal =
      totalAmount != null && Number.isFinite(Number(totalAmount)) ? Number(totalAmount) : null;
    const visibleSystems = filterVisibleDtoolsSystems(systems, displaySettings, {
      audience: previewVisibilityAudience,
    }).map((entry) => entry.system);
    const totals = buildDtoolsReviewProposalTotals({
      systems: visibleSystems,
      footerAdjustments: footer.adjustments,
    });
    const finalTotal = overrideTotal ?? totals.finalTotal;
    return resolveProposalCustomerPricingDisplay({
      totalAmount: finalTotal,
      retailPricingJson: retailPricingJson ?? undefined,
      computedProposalTotal: finalTotal,
    });
  }, [
    customerPricingProp,
    systems,
    footer.adjustments,
    totalAmount,
    retailPricingJson,
    displaySettings,
    previewVisibilityAudience,
  ]);

  const pdfCover = useMemo(
    () =>
      buildDtoolsPdfPackageCover({
        ...cover,
        revisionNumber: pdfPackageMeta?.revisionNumber,
        proposalDate: pdfPackageMeta?.proposalDate ?? createdAt,
        heroImageUrl: presentation?.heroImageUrl,
      }),
    [cover, pdfPackageMeta, createdAt, presentation?.heroImageUrl],
  );

  const pdfOptions = useMemo(
    () =>
      buildDtoolsPdfPackageOptions({
        isV2,
        systems,
        v2View,
        displaySettings,
        reviewContext: enrichedContext,
        audience: previewVisibilityAudience,
      }),
    [isV2, systems, v2View, displaySettings, enrichedContext, previewVisibilityAudience],
  );

  const quotationHeader = useMemo<DtoolsPdfQuotationHeaderInfo>(() => {
    return {
      customerName: pdfCover.customerName,
      address: pdfPackageMeta?.address?.trim() || null,
      proposalRef: pdfCover.proposalRef,
      revisionNumber: resolveDtoolsPdfPackageRevisionNumber(pdfPackageMeta?.revisionNumber),
      proposalDate: formatDtoolsPdfPackageDate(pdfPackageMeta?.proposalDate ?? createdAt),
    };
  }, [pdfCover.customerName, pdfCover.proposalRef, pdfPackageMeta, createdAt]);

  const summaryRows = useMemo((): DtoolsPdfSummarySystemRow[] => {
    return pdfOptions.map((option) => {
      const view = buildDtoolsReviewSystemView(option.system, enrichedContext, option.hierarchyIndex);
      return {
        key: option.key,
        label: option.sectionLabel,
        amountFormatted: formatDtoolsMoney(view.overview.systemTotal),
        isAlternative: option.isAlternative === true,
        isSelected: option.isAlternative !== true && alternativeOptions,
      };
    });
  }, [pdfOptions, enrichedContext, alternativeOptions]);

  const payableSystems = useMemo(() => {
    if (alternativeOptions) {
      return pdfOptions.filter((option) => !option.isAlternative).map((option) => option.system);
    }
    return pdfOptions.map((option) => option.system);
  }, [pdfOptions, alternativeOptions]);

  const grandTotalFormatted = useMemo(() => {
    if (customerPricing?.showFinalAgreedPrice === true && Number.isFinite(customerPricing.finalAgreedPrice)) {
      return formatDtoolsMoney(customerPricing.finalAgreedPrice);
    }
    // Prefer living commercial total (VO compose / header override) over raw system sum.
    if (customerPricing?.proposalTotal != null && Number.isFinite(customerPricing.proposalTotal)) {
      return formatDtoolsMoney(customerPricing.proposalTotal);
    }
    const totals = buildDtoolsReviewProposalTotals({
      systems: payableSystems,
      footerAdjustments: footer.adjustments,
    });
    return formatDtoolsMoney(totals.finalTotal);
  }, [customerPricing, payableSystems, footer.adjustments]);

  const grandTotalLabel =
    customerPricing?.showFinalAgreedPrice === true ? "Final Price" : "Grand Total";

  const disclaimerParagraphs = useMemo(() => {
    if (!presentation) return [];
    return sortedProposalDisclaimers(presentation).map((row) =>
      [row.name?.trim(), row.content?.trim()].filter(Boolean).join("\n"),
    );
  }, [presentation]);

  const linkBUrl = pdfPackageMeta?.linkBUrl?.trim() ?? "";
  const showItemPricing = !hideItemPricing;
  const showAlternativeNotes = alternativeOptions && summaryRows.some((row) => row.isAlternative);

  return (
    <DtoolsPdfPackageProvider>
      <div
        className={`dtools-proposal-pdf-package ${PROPOSAL_PDF_PACKAGE_SCREEN_HIDDEN_CLASS} space-y-10`}
        data-testid="dtools-proposal-pdf-package"
        data-proposal-ref={cover.proposalRef}
        data-pdf-logo-url={ClarityLogo}
        style={{ fontFamily: PROPOSAL_COPY_FONT }}
      >
        <DtoolsPdfPackageCoverPage cover={pdfCover} />

        <section
          className="pdf-page-break-after"
          data-testid="dtools-pdf-summary-section"
          data-pdf-pagination-title="Proposal Summary"
        >
          <div data-pdf-section-chrome>
            <DtoolsPdfProposalSummarySection
              header={quotationHeader}
              proposalTitle={pdfCover.proposalTitle}
              systemRows={summaryRows}
              grandTotalFormatted={grandTotalFormatted}
              grandTotalLabel={grandTotalLabel}
              paymentTerms={paymentTerms}
              proposalStatus={pdfPackageMeta?.proposalStatus}
              alternativeOptions={showAlternativeNotes}
              chromeOnly
            />
          </div>
          <div data-pdf-pagination-content>
            <DtoolsPdfProposalSummarySection
              header={quotationHeader}
              proposalTitle={pdfCover.proposalTitle}
              systemRows={summaryRows}
              grandTotalFormatted={grandTotalFormatted}
              grandTotalLabel={grandTotalLabel}
              paymentTerms={paymentTerms}
              proposalStatus={pdfPackageMeta?.proposalStatus}
              alternativeOptions={showAlternativeNotes}
              bodyOnly
            />
          </div>
        </section>

        {pdfOptions.map((option) => {
          const totalLabel =
            option.isAlternative || /^option\s+\d+/i.test(option.title) ? "Option Total" : "System Total";
          return (
            <section
              key={option.key}
              className="pdf-page-break-after"
              data-testid={`dtools-pdf-option-${option.key}`}
              data-pdf-pagination-title={option.title}
            >
              <div data-pdf-section-chrome>
                <DtoolsPdfOptionQuotationSection
                  system={option.system}
                  sectionLabel={option.sectionLabel}
                  header={quotationHeader}
                  reviewContext={enrichedContext}
                  optionIndex={option.hierarchyIndex}
                  showItemPricing={showItemPricing}
                  totalLabel={totalLabel}
                  chromeOnly
                />
              </div>
              <div data-pdf-pagination-content>
                <DtoolsPdfOptionQuotationSection
                  system={option.system}
                  sectionLabel={option.sectionLabel}
                  header={quotationHeader}
                  reviewContext={enrichedContext}
                  optionIndex={option.hierarchyIndex}
                  showItemPricing={showItemPricing}
                  totalLabel={totalLabel}
                  bodyOnly
                />
              </div>
            </section>
          );
        })}

        <section
          className="pdf-page-break-after"
          data-testid="dtools-pdf-terms-section"
          data-pdf-pagination-title="Terms and Conditions"
        >
          <div data-pdf-section-chrome>
            <DtoolsPdfTermsSection
              header={quotationHeader}
              paymentTerms={paymentTerms}
              createdAt={createdAt ?? pdfPackageMeta?.proposalDate}
              linkBExpiresAt={linkBExpiresAt}
              approvedAt={approvedAt}
              status={status}
              pipelineStage={pipelineStage}
              disclaimerParagraphs={disclaimerParagraphs}
              chromeOnly
            />
          </div>
          <div data-pdf-pagination-content>
            <DtoolsPdfTermsSection
              header={quotationHeader}
              paymentTerms={paymentTerms}
              createdAt={createdAt ?? pdfPackageMeta?.proposalDate}
              linkBExpiresAt={linkBExpiresAt}
              approvedAt={approvedAt}
              status={status}
              pipelineStage={pipelineStage}
              disclaimerParagraphs={disclaimerParagraphs}
              bodyOnly
            />
          </div>
        </section>

        <DtoolsPdfPackageClosingPage linkBUrl={linkBUrl} proposalRef={cover.proposalRef} />
      </div>
    </DtoolsPdfPackageProvider>
  );
}
